import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StripeCardElement, StripeElements } from '@stripe/stripe-js';
import { PaymentService, PaymentMethodDto } from '../services/payment.service';
import { ChargeService, PaymentHistoryItem } from '../services/charge.service';

// Provider-facing "collect a payment" screen: charge a customer a one-off amount,
// either against a card they have on file or a new card entered here. Uses the
// same Stripe Card Element style as the payment-methods screen.
@Component({
  selector: 'app-collect-payment',
  templateUrl: './collect-payment.component.html',
  styleUrls: ['./collect-payment.component.css']
})
export class CollectPaymentComponent implements OnInit, OnDestroy {
  @ViewChild('cardEl') cardEl?: ElementRef<HTMLDivElement>;

  customerUserId: number | null = null;
  amount: number | null = null;
  description = '';

  savedCards: PaymentMethodDto[] = [];
  selectedCardId = '';          // '' == enter a new card
  history: PaymentHistoryItem[] = [];

  loadingCustomer = false;
  processing = false;
  errorMessage = '';
  successMessage = '';

  private elements: StripeElements | null = null;
  private cardElement: StripeCardElement | null = null;

  constructor(
    private paymentService: PaymentService,
    private chargeService: ChargeService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.cardElement?.destroy();
  }

  loadCustomer(): void {
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.customerUserId) { return; }
    this.loadingCustomer = true;

    this.paymentService.getPaymentMethods(this.customerUserId).subscribe({
      next: cards => {
        this.savedCards = cards;
        const def = cards.find(c => c.isDefault);
        this.selectedCardId = def ? def.id : (cards.length ? cards[0].id : '');
        this.loadingCustomer = false;
        this.syncCardElement();
      },
      error: () => { this.loadingCustomer = false; this.errorMessage = 'Could not load this customer.'; }
    });

    this.chargeService.getHistory(this.customerUserId).subscribe({
      next: h => this.history = h,
      error: () => {}
    });
  }

  onCardChoiceChange(): void {
    this.syncCardElement();
  }

  private syncCardElement(): void {
    if (this.selectedCardId) {
      this.cardElement?.destroy();
      this.cardElement = null;
      return;
    }
    // Need a new-card element — mount after the DOM node renders.
    setTimeout(() => this.mountCard(), 0);
  }

  private async mountCard(): Promise<void> {
    if (this.cardElement || !this.cardEl) { return; }
    const stripe = await this.paymentService.getStripe();
    if (!stripe) { this.errorMessage = 'Payments are not configured yet.'; return; }
    this.elements = stripe.elements();
    this.cardElement = this.elements.create('card', {
      style: { base: { fontSize: '16px', color: '#2d3748', '::placeholder': { color: '#a0aec0' } } }
    });
    this.cardElement.mount(this.cardEl.nativeElement);
  }

  async charge(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.customerUserId) { this.errorMessage = 'Enter the customer user id.'; return; }
    if (!this.amount || this.amount <= 0) { this.errorMessage = 'Enter an amount greater than zero.'; return; }

    this.processing = true;

    // Saved card — the API confirms it off-session, one round trip.
    if (this.selectedCardId) {
      this.chargeService.charge({
        userId: this.customerUserId,
        amount: this.amount,
        description: this.description,
        paymentMethodId: this.selectedCardId
      }).subscribe({
        next: res => {
          this.processing = false;
          if (res.status === 'succeeded') {
            this.afterSuccess();
          } else {
            this.errorMessage = `Payment status: ${res.status} — the customer may need to authenticate the charge.`;
          }
        },
        error: err => { this.processing = false; this.errorMessage = err?.error?.message || 'Charge failed.'; }
      });
      return;
    }

    // New card — get a client secret, confirm with the Card Element.
    const stripe = await this.paymentService.getStripe();
    if (!stripe || !this.cardElement) {
      this.processing = false;
      this.errorMessage = 'Enter the card details first.';
      return;
    }

    this.chargeService.charge({
      userId: this.customerUserId,
      amount: this.amount,
      description: this.description
    }).subscribe({
      next: async res => {
        const result = await stripe.confirmCardPayment(res.clientSecret, {
          payment_method: { card: this.cardElement! }
        });
        this.processing = false;
        if (result.error) {
          this.errorMessage = result.error.message || 'Payment failed.';
          return;
        }
        if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
          this.afterSuccess();
        } else {
          this.errorMessage = `Payment status: ${result.paymentIntent?.status}.`;
        }
      },
      error: err => { this.processing = false; this.errorMessage = err?.error?.message || 'Could not start the payment.'; }
    });
  }

  private afterSuccess(): void {
    this.successMessage = `Charged $${this.amount?.toFixed(2)} successfully.`;
    this.amount = null;
    this.description = '';
    this.cardElement?.destroy();
    this.cardElement = null;
    this.loadCustomer();
  }
}
